import React from 'react';
import { SectionList } from 'react-native';
import { useTranslation } from 'react-i18next';
import { Box, Heading, Spinner, Text } from '@gluestack-ui/themed';
import FormField from '../../../common/components/forms/FormField';
import { useEventCalendarData } from '../hooks/useEventCalendarData';

/**
 * TaskBindingList Component
 * Renders a list of tasks that can be bound to a slot, filtered by event kind
 */
const TaskBindingList = ({ eventKindId }) => {
  const { t } = useTranslation();
  const { allTasks, moduleMap, tasksLoading } = useEventCalendarData();

  // Filter tasks that are compatible with selected event kind
  const compatibleTasks = React.useMemo(() => {
    if (!eventKindId) return [];

    return allTasks.filter((task) => {
      // Event delivery is orthogonal to task type. Prefer the backend flag and keep a fallback.
      const isDeliveredAsEvent = task?.isDeliveredAsEvent ?? Boolean(task?.eventConfig?.eventKindId);
      return isDeliveredAsEvent && task?.eventConfig?.eventKindId === eventKindId;
    });
  }, [allTasks, eventKindId]);

  // Deduplicate compatible tasks by ID (version-agnostic)
  const uniqueTasks = React.useMemo(() => {
    const taskMap = new Map();
    compatibleTasks.forEach((task) => {
      if (!taskMap.has(task.id)) {
        taskMap.set(task.id, task);
      }
    });
    return Array.from(taskMap.values());
  }, [compatibleTasks]);

  const sections = React.useMemo(() => {
    // Group unique tasks by moduleId
    const grouped = uniqueTasks.reduce((acc, task) => {
      const mId = task.moduleId || 'other';
      if (!acc[mId]) acc[mId] = { moduleId: mId, tasks: [] };
      acc[mId].tasks.push(task);
      return acc;
    }, {});

    return Object.values(grouped).map((g) => ({
      title: moduleMap[g.moduleId] || g.moduleId,
      moduleId: g.moduleId,
      data: g.tasks,
    }));
  }, [uniqueTasks, moduleMap]);

  const renderItem = ({ item, section }) => {
    // If there are multiple tasks for this module (in this section), append the version number to distinguish them
    const showVersion = section.data.length > 1;
    const label = showVersion && item.version ? `${item.title} (v${item.version})` : item.title;

    return (
      <Box ml="$6">
        <FormField
          name={`selectedTaskIds.${item.id}`}
          label={label}
          type="checkbox"
          defaultValue={false}
          horizontal
          fieldSize="sm"
          labelSize="sm"
        />
      </Box>
    );
  };

  const renderSectionHeader = ({ section }) => (
    <Heading size="xs" py="$1" numberOfLines={1}>
      {section.title}
    </Heading>
  );

  if (tasksLoading) return <Spinner />;

  if (!eventKindId) {
    return (
      <Text size="xs" color="$textLight500">
        {t('lecturer.eventCalendar.sidebar.selectEventKindFirst')}
      </Text>
    );
  }

  if (uniqueTasks.length === 0) {
    return (
      <Text size="xs" color="$textLight500">
        {t('lecturer.eventCalendar.sidebar.noCompatibleTasks')}
      </Text>
    );
  }

  return (
    <Box flex={1}>
      <SectionList
        sections={sections}
        keyExtractor={(item) => `${item.moduleId}-${item.version}-${item.id}`}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        style={{ flex: 1 }}
      />
    </Box>
  );
};

export default TaskBindingList;
